import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, Pipe, PipeTransform, ViewChild, inject } from "@angular/core";
import { ControlContainer, FormControl, FormControlStatus, FormGroup } from "@angular/forms";
import { MatAutocompleteTrigger } from "@angular/material/autocomplete";
import { MatOptionSelectionChange } from "@angular/material/core";
import { Observable, Subject } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";

import { CustomValidatorMessage, evaluate, isLgdTypeaheadFormControl, LgdErrorStateMatcher, LgdTypeaheadFormControl, } from "../lgdCommon";

@Component({
    selector: "lgd-typeahead",
    templateUrl: "./lgdTypeAhead.tmpl.html",
    styleUrls: ["./lgdTypeAhead.component.scss"],
    standalone: false
})
export class LgdTypeAheadComponent<TOption = unknown, TValue = TOption> implements OnInit, AfterViewInit, OnDestroy
{
    private readonly controlContainer = inject(ControlContainer);

    private readonly destructor$ = new Subject<void>();
    private _initialized: boolean;
    searchInputText: string;

    @ViewChild("typeahead") readonly inputElement: ElementRef<HTMLInputElement>;

    @Input() readonly controlName: string | number;
    @Input() readonly label: string;
    @Input() readonly hideLabel: boolean;
    @Input() readonly tooltip: string;
    @Input("options") readonly optionsSource: Observable<ReadonlyArray<TOption>>;
    @Input() readonly iconName: string;
    @Input() readonly iconTooltip: string;
    @Output() readonly iconAction = new EventEmitter<void>();

    /**
     * ! WARNING - This function CANNOT rely on `this` being the defining class.
     *
     * TODO - would it be cleaner to specify displayValueFn and valueFn through the LgdFormControlFactory.typeahead method instead of here?
     * @deprecated pass the function through the LgdFormControlConfig
     */
    @Input() readonly displayValueFn: (x: TOption) => string;

    /**
     * ! WARNING - This function CANNOT rely on `this` being the defining class.
     *
     * TODO - would it be cleaner to specify displayValueFn and valueFn through the LgdFormControlFactory.typeahead method instead of here?
     * @deprecated pass the function through the LgdFormControlConfig
     */
    @Input() readonly valueFn: (x: TOption) => TValue;

    @Output() readonly searchTextChanged = new EventEmitter<string>();

    @ViewChild("typeahead", { read: MatAutocompleteTrigger }) autoTrigger: MatAutocompleteTrigger;

    readonly errorStateMatcher = new LgdErrorStateMatcher();
    private readonly _typeAheadFormGroup: FormGroup;

    constructor()
    {
        this._typeAheadFormGroup = new FormGroup({});
    }

    get initialized(): boolean
    {
        return this._initialized;
    }

    get formGroup(): FormGroup
    {
        return this.controlContainer.control as FormGroup;
    }

    get formControl(): FormControl<TOption>
    {
        if (!this.formGroup || (this.controlName !== undefined && this.controlName == null))
        {
            throw new Error("Cannot access either formGroup or controlName.");
        }

        const controlNameStr = typeof this.controlName === "string" ? this.controlName : `${this.controlName}`;
        const control = this.formGroup.get(controlNameStr);
        if (!(control instanceof FormControl))
        {
            throw new Error(
                `controlName must reference a valid FormControl. controlName was ${JSON.stringify(this.controlName)}`
            );
        }
        return control as FormControl<TOption>;
    }

    get lgdFormControl(): LgdTypeaheadFormControl<TOption, TValue>
    {
        return isLgdTypeaheadFormControl<TOption, TValue>(this.formControl)
            ? this.formControl
            : undefined;
    }

    get typeAheadFormGroupName(): string
    {
        return `${this.controlName}_lgdTypeahead`;
    }

    get typeAheadFormGroup(): FormGroup
    {
        return this._typeAheadFormGroup;
    }

    get searchTextControlName(): string
    {
        return `${this.controlName}_searchText`;
    }

    get searchTextControl(): FormControl<TOption | string>
    {
        return this.typeAheadFormGroup.get(this.searchTextControlName) as FormControl<TOption | string>;
    }

    get searchText(): string
    {
        return this.searchInputText;
    }

    private _options: ReadonlyArray<Readonly<TOption>>;
    get options(): ReadonlyArray<Readonly<TOption>>
    {
        return this._options;
    }

    get autoActiveFirstOption(): boolean
    {
        return this.searchInputText !== "" && this.options?.length === 1;
    }

    private get freeForm(): boolean
    {
        return evaluate(this.lgdFormControl.data.freeForm);
    }

    hasCustomValidators: boolean;
    customErrors: ReadonlyArray<CustomValidatorMessage>;

    ngOnInit(): void
    {
        if (!this.optionsSource)
        {
            throw new Error(`options field is required. controlName was: ${JSON.stringify(this.controlName)}`);
        }

        if (this.lgdFormControl)
        {
            this.hasCustomValidators = !!this.lgdFormControl.data.customValidators;

            this.customErrors = this.hasCustomValidators
                ? this.lgdFormControl.data.customValidators.reduce(
                    (l, x) => l.concat(x.getErrorMessage(this.label)),
                    []
                )
                : [];
        }

        const initialValue = this.formControl.value;
        this.typeAheadFormGroup.addControl(
            this.searchTextControlName,
            new FormControl(
                {
                    value: initialValue,
                    disabled: this.formControl.disabled,
                },
                []
            )
        );
        this.formGroup.addControl(this.typeAheadFormGroupName, this.typeAheadFormGroup);
        this.searchInputText = this.getDisplayValue(initialValue);

        this.searchTextControl.updateValueAndValidity();

        this.formControl.valueChanges
            .pipe(takeUntil(this.destructor$))
            .subscribe(x =>
            {
                this.searchTextControl.setValue(x, { emitEvent: false });
            });

        this.lgdFormControl?.onChange
            .pipe(takeUntil(this.destructor$))
            .subscribe(() =>
            {
                this.searchTextControl.setValue(
                    this.getDisplayValue(this.formControl.value), { emitEvent: false });
            });

        this.searchTextControl.valueChanges
            .pipe(
                debounceTime(280),
                takeUntil(this.destructor$))
            .subscribe((searchTextValue: TOption | string) =>
            {
                if (this.freeForm)
                {
                    this.formControl.setValue(searchTextValue as TOption);

                    this.searchTextChanged.emit(
                        typeof searchTextValue === "string"
                            ? searchTextValue
                            : this.getDisplayValue(searchTextValue));
                    return;
                }

                if (typeof searchTextValue === "string" && searchTextValue !== "")
                {
                    if (this.searchInputText === searchTextValue)
                    {
                        return;
                    }

                    this.searchInputText = searchTextValue;
                    const selectedOption = this.options.find(x => this.getDisplayValue(x) === searchTextValue);
                    // Update the backing form control to keep its state in sync with the display state.
                    // if the searchTextValue doesn't match any options then the backing control needs to be cleared of any value
                    this.formControl.setValue(
                        selectedOption,
                        {
                            emitEvent: !!selectedOption,
                            emitModelToViewChange: !!selectedOption
                        });

                    // TODO - Why do we need to do this? we are already responding to this control's change and we haven't changed it's state...
                    this.searchTextControl.updateValueAndValidity();

                    this.searchTextChanged.emit(searchTextValue);
                    return;
                }

                const value = searchTextValue === "" ? undefined : searchTextValue;
                this.searchInputText = this.getDisplayValue(value);
                this.formControl.setValue(value);
                this.searchTextChanged.emit(this.searchInputText);
            });

        this.searchTextControl.statusChanges
            .pipe(
                debounceTime(500),
                takeUntil(this.destructor$))
            .subscribe((status: FormControlStatus) =>
            {
                console.log(`LgdTypeAheadComponent[${this.controlName}] | searchTextControl.statusChanges`, { status });
                this.formControl.updateValueAndValidity({ emitEvent: false });
            });

        this.formControl.registerOnDisabledChange((isDisabled: boolean) =>
        {
            if (isDisabled)
            {
                this.searchTextControl.disable({ emitEvent: false });
            }
            else
            {
                this.searchTextControl.enable({ emitEvent: false });
            }
        });

        this.optionsSource
            .pipe(takeUntil(this.destructor$))
            .subscribe((options: ReadonlyArray<TOption>) =>
            {
                this._options = options;
                this.searchTextControl.updateValueAndValidity({ emitEvent: false });
            });

        this.searchTextChanged.emit();

        this._initialized = true;
    }

    ngAfterViewInit(): void
    {
        if (!this.inputElement)
        {
            console.warn(`LgdTypeAheadComponent.ngAfterViewInit - ViewChild inputElement is not defined!`);
        }

        if (this.lgdFormControl)
        {
            this.lgdFormControl.onFocus.pipe(takeUntil(this.destructor$)).subscribe((shouldFocus: boolean) =>
            {
                if (shouldFocus)
                {
                    /**
                     * Calling in a setTimeout to avoid a bug in our current @angular version.
                     */
                    setTimeout(() =>
                    {
                        this.inputElement.nativeElement.focus();
                    });
                }
            });
        }

        this.autoTrigger.panelClosingActions
            .pipe(takeUntil(this.destructor$))
            .subscribe((_x: MatOptionSelectionChange) =>
            {
                if (this.autoTrigger.activeOption)
                {
                    const value = this.autoTrigger.activeOption.value;
                    this.searchTextControl.setValue(this.getDisplayValue(value), { emitEvent: false });

                    this.autoTrigger.writeValue(value);
                }
            });
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    getDisplayValue(x: TOption): string
    {
        const fn = this.lgdFormControl?.data?.displayValueFn || this.displayValueFn || defaultToValueFn;
        const displayValue = fn(x);
        return displayValue;
    }

    getDisplayValueFn(): (x: TOption) => string
    {
        return (x: TOption) =>
        {
            // The MatAutocompleteTrigger may have already converted the model object a string so if we already have string, just use it....
            if (typeof x === "string")
            {
                return x;
            }
            const fn = this.lgdFormControl?.data?.displayValueFn || this.displayValueFn || defaultToValueFn;

            return fn(x);
        };
    }

    getValue(x: TOption): TValue
    {
        return this.getValueFn()(x);
    }

    private getValueFn(): (x: TOption) => TValue
    {
        if (!!this.lgdFormControl && !!this.lgdFormControl.data.valueFn)
        {
            return this.lgdFormControl.data.valueFn;
        }

        return this.valueFn || defaultToValueFn;
    }

    hasError(id: string): boolean
    {
        return this.formControl.hasError(id);
    }

    get isInvalid(): boolean
    {
        return this.formControl.errors !== null;
    }

    get isRequired(): boolean
    {
        return !!this.lgdFormControl && evaluate(this.lgdFormControl.data.required);
    }

    get helperTextSpacer(): boolean
    {
        return !this.hasError("required") && !this.hasCustomValidators;
    }

    get hasIcon(): boolean
    {
        return !!this.iconName;
    }

    iconActionClick(): void
    {
        this.iconAction.emit();
    }

    clearSearchText(): void
    {
        this.searchTextControl.setValue(undefined);
    }
}

function defaultToValueFn<TIn = unknown, TOut = unknown>(x: TIn): TOut
{
    return x as unknown as TOut;
}

@Pipe({
    name: "highlight",
    standalone: false
})
export class HighlightPipe implements PipeTransform
{
    transform(text: string, search: string | undefined): string
    {
        if (!search)
        {
            return text;
        }

        const pattern = search
            .replace(/[-[\]/{}()*+?.\\^$|]/g, "\\$&")
            .split(" ")
            .filter(t => t.length > 0)
            .join("|");
        const regex = new RegExp(pattern, "gi");

        return text.replace(regex, match => `<b>${match}</b>`);
    }
}
